import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'
import { installationCreateGoodsFamilyValues } from '@/features/installations/components/installation-create/constants'
import type {
  InstallationCreateFormInput,
  InstallationGoodsFamily,
} from '@/features/installations/installation-create.schema'

const reviewRowClassName =
  'grid gap-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-[minmax(0,220px)_1fr] sm:gap-4'

type InstallationCreateReviewSummaryProps = {
  values: InstallationCreateFormInput
  requiresLocalNameAndAddress: boolean
  placeOfRegistration: string
  translateField: (key: string) => string
  translateGoodsFamily: (value: InstallationGoodsFamily) => string
}

function formatCoordinate(coordinate: unknown): string {
  if (typeof coordinate === 'number' && Number.isFinite(coordinate)) {
    return String(coordinate)
  }
  return '—'
}

export function InstallationCreateReviewSummary({
  values,
  requiresLocalNameAndAddress,
  placeOfRegistration,
  translateField,
  translateGoodsFamily,
}: InstallationCreateReviewSummaryProps) {
  const { t: tCountryName } = useTranslation('countriesByIsoAlpha2')
  const descriptionText = (values.description ?? '').trim()
  const goodsFamilyValue = values.goodsFamily as InstallationGoodsFamily | ''
  const countryCodeLower = (values.country ?? '').trim().toLowerCase()
  const countryDisplayName =
    countryCodeLower.length === 2 ? tCountryName(countryCodeLower) : '—'
  const showStreet2 = (values.street2 ?? '').trim() !== ''
  const showLocalStreet2 = (values.localStreet2 ?? '').trim() !== ''

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-sm font-semibold tracking-tight">
          {translateField('create.sections.general')}
        </h3>
        <dl className="mt-2 divide-y divide-border rounded-md border border-border px-4">
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.name.label')}
            </dt>
            <dd className="text-sm font-medium">{values.name || '—'}</dd>
          </div>
          {requiresLocalNameAndAddress ? (
            <div className={reviewRowClassName}>
              <dt className="text-muted-foreground flex items-center gap-2 text-sm">
                {translateField('create.fields.localName.label')}
                {placeOfRegistration ? (
                  <ReactCountryFlag countryCode={placeOfRegistration} svg />
                ) : null}
              </dt>
              <dd className="text-sm font-medium">
                {(values.localName ?? '').trim() || '—'}
              </dd>
            </div>
          ) : null}
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.goodsFamily.label')}
            </dt>
            <dd className="text-sm font-medium">
              {goodsFamilyValue &&
              installationCreateGoodsFamilyValues.includes(goodsFamilyValue)
                ? translateGoodsFamily(goodsFamilyValue)
                : '—'}
            </dd>
          </div>
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.description.label')}
            </dt>
            <dd className="whitespace-pre-wrap text-sm font-medium">
              {descriptionText.length > 0 ? descriptionText : '—'}
            </dd>
          </div>
        </dl>
      </section>

      <section>
        <h3 className="text-sm font-semibold tracking-tight">
          {translateField('create.sections.addressLatin')}
        </h3>
        <dl className="mt-2 divide-y divide-border rounded-md border border-border px-4">
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.country.label')}
            </dt>
            <dd className="text-sm font-medium">{countryDisplayName}</dd>
          </div>
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.street.label')}
            </dt>
            <dd className="text-sm font-medium">{values.street || '—'}</dd>
          </div>
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.streetNumber.label')}
            </dt>
            <dd className="text-sm font-medium">{values.streetNumber || '—'}</dd>
          </div>
          {showStreet2 ? (
            <div className={reviewRowClassName}>
              <dt className="text-muted-foreground text-sm">
                {translateField('create.fields.street2.label')}
              </dt>
              <dd className="text-sm font-medium">{(values.street2 ?? '').trim()}</dd>
            </div>
          ) : null}
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.postCode.label')}
            </dt>
            <dd className="text-sm font-medium">{(values.postCode ?? '').trim() || '—'}</dd>
          </div>
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.city.label')}
            </dt>
            <dd className="text-sm font-medium">{values.city || '—'}</dd>
          </div>
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.poBox.label')}
            </dt>
            <dd className="text-sm font-medium">{(values.poBox ?? '').trim() || '—'}</dd>
          </div>
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.longitude.label')}
            </dt>
            <dd className="text-sm font-medium">{formatCoordinate(values.longitude)}</dd>
          </div>
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.latitude.label')}
            </dt>
            <dd className="text-sm font-medium">{formatCoordinate(values.latitude)}</dd>
          </div>
          <div className={reviewRowClassName}>
            <dt className="text-muted-foreground text-sm">
              {translateField('create.fields.unlocode.label')}
            </dt>
            <dd className="text-sm font-medium">
              {(values.unlocode ?? '').trim() || '—'}
            </dd>
          </div>
        </dl>
      </section>

      {requiresLocalNameAndAddress ? (
        <section>
          <h3 className="text-sm font-semibold tracking-tight">
            {translateField('create.sections.addressLocal')}
          </h3>
          <dl className="mt-2 divide-y divide-border rounded-md border border-border px-4">
            <div className={reviewRowClassName}>
              <dt className="text-muted-foreground text-sm">
                {translateField('create.fields.localStreet.label')}
              </dt>
              <dd className="text-sm font-medium">{values.localStreet || '—'}</dd>
            </div>
            <div className={reviewRowClassName}>
              <dt className="text-muted-foreground text-sm">
                {translateField('create.fields.localStreetNumber.label')}
              </dt>
              <dd className="text-sm font-medium">{values.localStreetNumber || '—'}</dd>
            </div>
            {showLocalStreet2 ? (
              <div className={reviewRowClassName}>
                <dt className="text-muted-foreground text-sm">
                  {translateField('create.fields.localStreet2.label')}
                </dt>
                <dd className="text-sm font-medium">
                  {(values.localStreet2 ?? '').trim()}
                </dd>
              </div>
            ) : null}
            <div className={reviewRowClassName}>
              <dt className="text-muted-foreground text-sm">
                {translateField('create.fields.localPostCode.label')}
              </dt>
              <dd className="text-sm font-medium">
                {(values.localPostCode ?? '').trim() || '—'}
              </dd>
            </div>
            <div className={reviewRowClassName}>
              <dt className="text-muted-foreground text-sm">
                {translateField('create.fields.localCity.label')}
              </dt>
              <dd className="text-sm font-medium">{values.localCity || '—'}</dd>
            </div>
            <div className={reviewRowClassName}>
              <dt className="text-muted-foreground text-sm">
                {translateField('create.fields.localPoBox.label')}
              </dt>
              <dd className="text-sm font-medium">
                {(values.localPoBox ?? '').trim() || '—'}
              </dd>
            </div>
          </dl>
        </section>
      ) : null}
    </div>
  )
}
